// store/modules/activityLogs.js
import api from '@/api/api'

export default {
    namespaced: true,
    state: {
        logs: [],
        currentLog: null,
        statistics: {
            totalLogs: 0,
            logsByStatus: [],
            logsByRole: [],
            topActions: [],
            recentErrors: [],
            activeUsers: [],
            ipAddressStats: []
        },
        loading: false,
        error: null,
        pagination: {
            currentPage: 1,
            totalPages: 1,
            total: 0,
            limit: 50
        },
        filters: {
            userRole: '',
            userId: '',
            action: '',
            resourceType: '',
            status: '',
            ipAddress: '',
            startDate: null,
            endDate: null,
            groupBy: null
        }
    },
    getters: {
        allLogs: (state) => state.logs,
        currentLog: (state) => state.currentLog,
        statistics: (state) => state.statistics,
        loading: (state) => state.loading,
        error: (state) => state.error,
        pagination: (state) => state.pagination,
        filters: (state) => state.filters,

        // Get grouped data if groupBy is active
        groupedData: (state) => {
            if (state.filters.groupBy && Array.isArray(state.logs)) {
                return state.logs;
            }
            return null;
        }
    },
    mutations: {
        SET_LOGS(state, logs) {
            state.logs = logs;
        },
        SET_CURRENT_LOG(state, log) {
            state.currentLog = log;
        },
        SET_STATISTICS(state, statistics) {
            state.statistics = statistics;
        },
        SET_LOADING(state, status) {
            state.loading = status;
        },
        SET_ERROR(state, error) {
            state.error = error;
        },
        CLEAR_ERROR(state) {
            state.error = null;
        },
        SET_PAGINATION(state, pagination) {
            state.pagination = { ...state.pagination, ...pagination };
        },
        SET_FILTERS(state, filters) {
            state.filters = { ...state.filters, ...filters };
        },
        RESET_FILTERS(state) {
            state.filters = {
                userRole: '',
                userId: '',
                action: '',
                resourceType: '',
                status: '',
                ipAddress: '',
                startDate: null,
                endDate: null,
                groupBy: null
            };
            state.pagination.currentPage = 1;
        }
    },
    actions: {
        // Fetch activity logs with filters
        async fetchLogs({ commit, state }, params = {}) {
            try {
                commit('SET_LOADING', true);
                commit('CLEAR_ERROR');

                // Merge params with current filters
                const queryParams = {
                    page: params.page || state.pagination.currentPage,
                    limit: state.pagination.limit,
                    ...state.filters,
                    ...params
                };

                // Remove empty values
                Object.keys(queryParams).forEach(key => {
                    if (!queryParams[key] && queryParams[key] !== 0) {
                        delete queryParams[key];
                    }
                });

                const response = await api.get('/activity-logs', { params: queryParams });

                commit('SET_LOGS', response.data.data);
                commit('SET_PAGINATION', {
                    currentPage: response.data.currentPage,
                    totalPages: response.data.totalPages,
                    total: response.data.total
                });

                return response.data;
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Failed to fetch activity logs';
                commit('SET_ERROR', errorMessage);
                throw new Error(errorMessage);
            } finally {
                commit('SET_LOADING', false);
            }
        },

        // Fetch a specific log by ID
        async fetchLogById({ commit }, id) {
            try {
                commit('SET_LOADING', true);
                commit('CLEAR_ERROR');

                const response = await api.get(`/activity-logs/${id}`);
                commit('SET_CURRENT_LOG', response.data.data);
                return response.data.data;
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Failed to fetch activity log';
                commit('SET_ERROR', errorMessage);
                throw new Error(errorMessage);
            } finally {
                commit('SET_LOADING', false);
            }
        },

        // Fetch activity statistics
        async fetchStatistics({ commit, state }) {
            try {
                commit('SET_LOADING', true);
                commit('CLEAR_ERROR');

                const params = {};
                if (state.filters.startDate) params.startDate = state.filters.startDate;
                if (state.filters.endDate) params.endDate = state.filters.endDate;

                const response = await api.get('/activity-logs/statistics', { params });
                commit('SET_STATISTICS', response.data.data);
                return response.data.data;
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Failed to fetch statistics';
                commit('SET_ERROR', errorMessage);
                throw new Error(errorMessage);
            } finally {
                commit('SET_LOADING', false);
            }
        },

        // Export activity logs
        async exportLogs({ state }, format = 'json') {
            try {
                const queryParams = {
                    format,
                    ...state.filters
                };

                // Remove empty values
                Object.keys(queryParams).forEach(key => {
                    if (!queryParams[key] && queryParams[key] !== 0) {
                        delete queryParams[key];
                    }
                });

                const response = await api.get('/activity-logs/export', {
                    params: queryParams,
                    responseType: format === 'csv' ? 'blob' : 'json'
                });

                if (format === 'csv') {
                    // Create download link for CSV
                    const blob = new Blob([response.data], { type: 'text/csv' });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', `activity-logs-${new Date().toISOString().split('T')[0]}.csv`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                }

                return response.data;
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Failed to export logs';
                throw new Error(errorMessage);
            }
        },

        // Update filters
        updateFilters({ commit, dispatch }, filters) {
            commit('SET_FILTERS', filters);
            commit('SET_PAGINATION', { currentPage: 1 }); // Reset to first page
            return dispatch('fetchLogs');
        },

        // Reset filters
        resetFilters({ commit, dispatch }) {
            commit('RESET_FILTERS');
            return dispatch('fetchLogs');
        },

        // Change page
        changePage({ commit, dispatch }, page) {
            commit('SET_PAGINATION', { currentPage: page });
            return dispatch('fetchLogs');
        }
    }
}
